/**
 * MathChatPanel — chat panel for the math agent with conversation history sidebar.
 * Mirrors V7AgentChatPanel's embedded pattern.
 * Talks to /api/v7-math/agent endpoints.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import MathMessageBubble from './MathMessageBubble';
import styles from './MathChatPanel.module.css';

const API_BASE = '/api/v7-math/agent';

const WELCOME_MESSAGE = {
    id: 'welcome',
    role: 'assistant',
    content: `Hello! I'm your **Math Discussion Assistant**.

I can help you:
- **Analyze** how students collaborated on math problems
- **Verify** mathematical claims and solutions
- **Explain** concepts and give hints
- **Assess** collaboration quality

What would you like to explore?`,
    isWelcome: true,
    created_at: new Date().toISOString(),
};

const MathChatPanel = ({ sessionDeviceId }) => {
    const [messages, setMessages] = useState([WELCOME_MESSAGE]);
    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sidebarVisible, setSidebarVisible] = useState(false);

    const messagesEndRef = useRef(null);
    const messagesAreaRef = useRef(null);
    const inputRef = useRef(null);
    const loadedFromHistory = useRef(false);

    // Auto-scroll: to top when loading history, to bottom when chatting
    useEffect(() => {
        if (loadedFromHistory.current) {
            loadedFromHistory.current = false;
            messagesAreaRef.current?.scrollTo({ top: 0 });
        } else {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Load conversations on mount
    useEffect(() => {
        loadConversations();
    }, []);

    // Reset conversation when session changes
    useEffect(() => {
        startNewConversation();
    }, [sessionDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadConversations = async () => {
        try {
            const res = await fetch(`${API_BASE}/conversations`);
            if (!res.ok) return;
            const data = await res.json();
            setConversations(data.conversations || []);
        } catch (_) {}
    };

    const loadConversation = async (conversationId) => {
        try {
            setIsLoading(true);
            const res = await fetch(`${API_BASE}/conversations/${conversationId}`);
            if (!res.ok) throw new Error('Failed to load');
            const data = await res.json();
            loadedFromHistory.current = true;
            setMessages((data.messages || []).map(m => ({
                ...m,
                isUser: m.role === 'user',
            })));
            setActiveConversationId(conversationId);
        } catch (err) {
            setError('Failed to load conversation');
        } finally {
            setIsLoading(false);
        }
    };

    const startNewConversation = () => {
        setMessages([{ ...WELCOME_MESSAGE, id: `welcome-${Date.now()}` }]);
        setActiveConversationId(null);
        setError(null);
        inputRef.current?.focus();
    };

    const handleDeleteConversation = async (conversationId) => {
        try {
            await fetch(`${API_BASE}/conversations/${conversationId}`, { method: 'DELETE' });
            setConversations(prev => prev.filter(c => c.id !== conversationId));
            if (activeConversationId === conversationId) {
                startNewConversation();
            }
        } catch (_) {
            setError('Failed to delete conversation');
        }
    };

    const sendMessage = useCallback(async () => {
        const query = inputValue.trim();
        if (!query || isLoading) return;

        setInputValue('');
        setError(null);

        const userMsg = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: query,
            isUser: true,
            created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev.filter(m => !m.isWelcome), userMsg]);
        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE}/query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query,
                    conversation_id: activeConversationId,
                    session_device_id: sessionDeviceId ? parseInt(sessionDeviceId) : null,
                }),
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const data = await response.json();

            // Track conversation ID
            if (data.conversation_id && !activeConversationId) {
                setActiveConversationId(data.conversation_id);
                loadConversations(); // Refresh sidebar
            }

            const assistantMsg = {
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: data.response || data.answer || 'No response received.',
                tools_used: data.tools_used || [],
                created_at: new Date().toISOString(),
            };
            setMessages(prev => [...prev, assistantMsg]);
        } catch (err) {
            setError(err.message);
            setMessages(prev => [...prev, {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Sorry, I encountered an error: ${err.message}. Please try again.`,
                isError: true,
                created_at: new Date().toISOString(),
            }]);
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, activeConversationId, sessionDeviceId]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Group conversations by date
    const groupedConversations = (() => {
        const groups = {};
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 86400000).toDateString();

        conversations.forEach(conv => {
            const date = new Date(conv.updated_at || conv.created_at).toDateString();
            let label;
            if (date === today) label = 'Today';
            else if (date === yesterday) label = 'Yesterday';
            else label = date;
            if (!groups[label]) groups[label] = [];
            groups[label].push(conv);
        });
        return groups;
    })();

    return (
        <div className={styles.chatPanel}>
            {/* Sidebar — overlay, hidden by default */}
            <div className={`${styles.sidebar} ${!sidebarVisible ? styles.sidebarHidden : ''}`}>
                <div className={styles.sidebarHeader}>
                    <button className={styles.newChatButton} onClick={() => { startNewConversation(); setSidebarVisible(false); }}>
                        + New Chat
                    </button>
                    <button
                        className={styles.sidebarCloseBtn}
                        onClick={() => setSidebarVisible(false)}
                        title="Hide history"
                    >
                        &#10005;
                    </button>
                </div>

                <div className={styles.sidebarList}>
                    {conversations.length === 0 ? (
                        <div className={styles.emptySidebar}>No conversations yet</div>
                    ) : (
                        Object.entries(groupedConversations).map(([date, convos]) => (
                            <div key={date}>
                                <div className={styles.dateGroup}>{date}</div>
                                {convos.map(conv => (
                                    <div
                                        key={conv.id}
                                        className={`${styles.conversationItem} ${
                                            conv.id === activeConversationId ? styles.conversationActive : ''
                                        }`}
                                        onClick={() => { loadConversation(conv.id); setSidebarVisible(false); }}
                                    >
                                        <span className={styles.conversationTitle}>
                                            {conv.title || 'New conversation'}
                                        </span>
                                        <div className={styles.conversationActions}>
                                            <button
                                                className={styles.conversationBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm('Delete this conversation?')) {
                                                        handleDeleteConversation(conv.id);
                                                    }
                                                }}
                                                title="Delete"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className={styles.mainChat}>
                <div className={styles.header}>
                    <button
                        className={styles.historyToggleBtn}
                        onClick={() => setSidebarVisible(v => !v)}
                        title={sidebarVisible ? 'Hide history' : 'Show history'}
                    >
                        &#9776;
                    </button>
                    <h2 className={styles.title}>Agent Chat</h2>
                </div>

                <div className={styles.messagesArea} ref={messagesAreaRef}>
                    <div className={styles.messagesContainer}>
                        {messages.map((msg) => (
                            <MathMessageBubble key={msg.id} message={msg} />
                        ))}
                        {isLoading && (
                            <div className={styles.loadingIndicator}>
                                <div className={styles.typingDots}>
                                    <span></span><span></span><span></span>
                                </div>
                                <span className={styles.loadingText}>Thinking...</span>
                            </div>
                        )}
                        {error && !isLoading && (
                            <div className={styles.errorMessage}>{error}</div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                <div className={styles.inputWrapper}>
                    <div className={styles.inputContainer}>
                        <form className={styles.inputArea} onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
                            <textarea
                                ref={inputRef}
                                className={styles.input}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask about the math discussion..."
                                rows={1}
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                className={styles.sendButton}
                                disabled={isLoading || !inputValue.trim()}
                            >
                                {isLoading ? '...' : 'Send'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MathChatPanel;
